import { useEffect, useMemo, useState } from 'react';

import { OfferOption, SelectedOffer } from '@utils/backend-api/subscribe';
import { WIDTH_SM } from '@utils/constants';

import useWindowSize from './useWindowSize';

const useDualOfferFunctionality = ({
  defaultSelectedOffer,
  offers,
}: {
  defaultSelectedOffer: SelectedOffer;
  offers: OfferOption[];
}) => {
  const [selectedOffer, setSelectedOffer] = useState(defaultSelectedOffer);
  const { width: windowWidth } = useWindowSize();
  const isMobile = windowWidth < WIDTH_SM;

  const verticalOffers = useMemo(
    () => offers.filter((offer) => offer.isPro || (offer.isAnnual && !offer.isYp)),
    [offers]
  );

  const horizontalOffers = useMemo(
    () =>
      offers
        .filter((offer) => offer.isYp || offer.isMonthly)
        .sort((a, b) => (a.isYp ? -1 : b.isYp ? 1 : 0)),
    [offers]
  );

  const [midPoint, setMidPoint] = useState('50%');

  useEffect(() => {
    if (defaultSelectedOffer.isYp || defaultSelectedOffer.isMonthly) {
      setSelectedOffer(verticalOffers[0]);
    }
  }, [defaultSelectedOffer, verticalOffers]);

  useEffect(() => {
    if (isMobile && verticalOffers.length >= 2) {
      const first = document.getElementById(verticalOffers[1].code);
      const second = document.getElementById(verticalOffers[0].code);

      if (!first || !second) return;

      let rafId: number;

      const updateMidpoint = () => {
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          const firstBottom = first.offsetTop + first.offsetHeight;
          const secondTop = second.offsetTop;
          const middleY = (firstBottom + secondTop) / 2;
          setMidPoint(`${middleY}px`);
        });
      };

      const resizeObserver = new ResizeObserver(updateMidpoint);
      resizeObserver.observe(first);
      resizeObserver.observe(second);

      const mutationObserver = new MutationObserver(updateMidpoint);
      mutationObserver.observe(first, { childList: true, subtree: true });
      mutationObserver.observe(second, { childList: true, subtree: true });

      updateMidpoint();

      return () => {
        cancelAnimationFrame(rafId);
        resizeObserver.disconnect();
        mutationObserver.disconnect();
      };
    }
  }, [isMobile, verticalOffers]);

  return {
    horizontalOffers,
    isMobile,
    midPoint,
    selectedOffer,
    setSelectedOffer,
    verticalOffers,
  };
};

export default useDualOfferFunctionality;
